import { Injectable, inject } from '@angular/core';
import { Observable, map, shareReplay, catchError, of } from 'rxjs';
import { BaseTwcService } from './twc-base.data.service';
import { TwcParams, TwcResponseData } from './twc.data.types';
import { getLatestTimeFromMap } from '../twc.utils';
import { TwcTimeValidationService } from '../twc-time-validation.service';
import { NGXLogger } from 'ngx-logger';
import { TwcVectorTileInfo } from './twc-tile-info.model';

export const VECTOR_TILE_ROOT_URL =
  'https://api.weather.com/v2/vector-api/products'; // VectorTile only.

@Injectable({ providedIn: 'root' })
export class TwcVectorTileDataService extends BaseTwcService<TwcResponseData> {
  private readonly _timeValidationService = inject(TwcTimeValidationService);
  private readonly _logger = inject(NGXLogger);

  getTwcData$({
    twcProduct,
    flightLevelValues,
    updatedTimeInSeconds
  }: TwcParams): Observable<TwcResponseData> {
    const params = this.getHttpParams({
      apiKey: this.twcApiKey ?? 'api key not set',
      'max-times': '12'
    });

    return this.httpClient
      .get<TwcVectorTileInfo>(
        `${VECTOR_TILE_ROOT_URL}/${twcProduct.defaultProductId}/info`,
        { params }
      )
      .pipe(
        map((info) => {
          // Extract times from response
          const times = info.products[twcProduct.defaultProductId].time;

          // Create time map
          const timeMap = this.createTimeMap(
            times,
            twcProduct.series === 'Forecast'
          );

          // Get the latest time from the map
          const tsInSeconds = getLatestTimeFromMap(timeMap)?.start ?? '';

          // Update the time validation service for this product
          this._timeValidationService.updateProductTimes(
            twcProduct.defaultProductId,
            times
          );

          // Also update any flight level product IDs
          Object.values(twcProduct.flightLevelProductIds).forEach(
            (productId) => {
              if (
                productId !== twcProduct.defaultProductId &&
                info.products[productId]?.time
              ) {
                this._timeValidationService.updateProductTimes(
                  productId,
                  info.products[productId].time
                );
              }
            }
          );

          return {
            flightLevelInfo: twcProduct.flightLevelProductIds,
            tsInSeconds,
            timeMap,
            flightLevelValues,
            twcProduct,
            updatedTimeInSeconds
          };
        }),
        catchError((error) => {
          this._logger.error('Error fetching TWC Vector Tile data:', error);
          return of({
            flightLevelInfo: twcProduct.flightLevelProductIds,
            tsInSeconds: '',
            timeMap: this.createTimeMap([], twcProduct.series === 'Forecast'),
            flightLevelValues,
            twcProduct,
            updatedTimeInSeconds
          });
        }),
        shareReplay(1)
      );
  }
}
